import React, { useRef } from "react";
import { GamesSearchInput } from "@/v2-components/pages/games/search/GamesSearchInput";
import GamesTabMenu from "@/v2-components/pages/games/GameTabMenu";
import GlobalNavigationBar from "@/v2-components/global-navigation-bar";
import PromoBanner from "@/components/banner/PromoBanner";
import { cn } from "@/libs/utils";
import { usePathname } from "next/navigation";
import { useIsMobileView } from "@/contexts/ResponsiveProvider";
import { useRouter } from "next/router";
import CarotLeftIcon from "@/v2-components/core/icon/carot-left-icon";
import PageTemplate from "@/components/templates/PageTemplate";
import ReferralBanner from "@/components/banner/ReferralBanner";
import { useResizeObserver } from "@/hooks/useResizeObserver";
import { useHiddenOnScroll } from "@/hooks/useHiddenOnScroll";
import { isProgrammaticScroll } from "@/v2-components/pages/games/scroll-to-results";
import { useLocale } from "@/store/locale-store";
import SuggestLocale from "@/components/SuggestLocale";

type Props = {
  children?: React.ReactNode;
  countryCode: string;
  /**
   * 스티키 띠에 들어갈 탭바를 갈아끼운다. 기본값은 기존 「검색창 + 플랫폼 9탭」이다.
   *
   * PC/VR Games 리뉴얼(#5407)의 Overview 는 그 자리에 **3탭 서브내비**(Overview /
   * PC Games / Software)가 온다. 템플릿을 복제하지 않는 이유는 이 컴포넌트가 스티키
   * 숨김·배너·SuggestLocale·z 레이어를 들고 있어서다 — 복제하면 `docs/pitfalls.md`
   * #31·#36 이 고쳐둔 것을 두 벌 관리하게 된다.
   */
  tabBar?: React.ReactNode;
};

const GamesPageTemplate = ({ children, countryCode, tabBar }: Props) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const { height: headerHeight } = useResizeObserver(headerRef);
  const { targetRef, hidden, containerProps } =
    useHiddenOnScroll<HTMLDivElement>({
      throttleMs: 100,
      enabled: true,
      threshold: 80,
      /**
       * ★**정렬이 일으킨 스크롤에는 반응하지 않는다** (#5672).
       *
       * `useKeepResultsInView` 의 정렬은 결과를 띠 아래로 되돌리려고 **위로** 스크롤한다.
       * 그걸 사용자 의도로 읽으면 헤더가 나오고, 아래 `stickyTop` 이 `0 → headerHeight`
       * 로 바뀌어 **스티키 띠가 통째로 내려간다** — 그 안에 도킹된 검색창이 한 프레임에
       * 137px 뛰어내렸다(실측).
       *
       * `GameDetailPageTemplate` 은 이걸 넘기지 않는다 — 그쪽 프로그램적 스크롤(목차
       * 클릭)은 사용자가 방금 누른 것이라 헤더가 나오는 편이 맞다.
       */
      suspended: isProgrammaticScroll,
    });
  const stickyTop = hidden ? 0 : headerHeight;
  const locale = useLocale();
  const canSuggeset = !!countryCode && locale.countryCode !== countryCode;

  return (
    <PageTemplate>
      <PageTemplate.Content useCustomNav={true}>
        {/* containerProps — 숨을 때 내용물만 transform 으로 치우므로 이 래퍼 박스는 높이
            그대로 top:0 에 남는다. 같은 순간 아래 띠도 올라오므로, 래퍼가 포인터를 먹으면
            탭이 전부 클릭 불가가 된다 (#5162). z 로 풀면 #25(쿠폰 말풍선 잘림)가 재발하니
            건드리지 말 것. 상세는 docs/pitfalls.md #36(스크롤로 숨는 스티키 헤더). */}
        <div
          {...containerProps}
          ref={headerRef}
          data-testid="games-sticky-header"
          className="sticky top-0 z-30"
        >
          <div
            ref={targetRef}
            data-testid="games-header-animated"
            className="transition-[transform,opacity] duration-200"
          >
            {/* IP 기반 국가 변경 제안(SuggestLocale)은 스티키 상태에서 노출하지 않는다
                (2026-08-10 결정) → 함께 숨도록 애니메이션 래퍼 안. docs/pitfalls.md #36(스크롤로 숨는 스티키 헤더) */}
            {canSuggeset && <SuggestLocale countryCode={countryCode} />}
            <ReferralBanner />
            <GlobalNavigationBar />
            <PromoBanner />
          </div>
        </div>
        {/* z-20 — 헤더(z-30)보다 **아래**여야 한다. GNB 쿠폰 말풍선이 헤더 박스 밖으로 넘쳐
            이 띠 위에 걸치는데, 같은 z-30 이면 DOM 뒤인 이 띠가 이겨 말풍선이 잘린다.
            GNB 레이어 스케일은 docs/pitfalls.md #31(헤더 래퍼의 z) 참조. */}
        <div
          data-testid="games-sticky-tabbar"
          /* ★**`pt-4` 를 여기 두지 않는다** (#5568).

             모바일에서 GNB 아래 16px 빈 띠가 생겼다. 시안(`29942:17594` `subnav-v2 / mobile`)은
             서브내비가 **GNB 바로 아래**(`y=64` = GNB 높이 실측 64)에서 시작하고 높이 48 이다 —
             여백 0 이다. 실측(dev): 390·768 에서 `틈 16`, 1440 에서 `틈 0`.

             그런데 이 `pt-4` 는 리뉴얼 전부터 있던 것으로, **폴백 `GamesTabWrap`**(검색창 +
             카테고리 탭이 `gap-3` 로 쌓이는 옛 배치)의 여백이다. 여기서 지우면 그쪽 9페이지가
             GNB 에 붙으므로 **폴백 안으로 옮겼다.** 그래야:

               tabBar 전달(리뉴얼 5페이지 + 게임 상세)  →  0   시안대로
               폴백(레거시 9페이지)                      →  16  종전 그대로 */
          className="sticky z-20 bg-white"
          style={{ top: `${stickyTop}px` }}
        >
          {tabBar ?? <GamesTabWrap />}
          {!tabBar && (
            <hr className="absolute bottom-0 hidden h-px w-full laptop:flex" />
          )}
        </div>
        {children}
      </PageTemplate.Content>
    </PageTemplate>
  );
};

export default GamesPageTemplate;

const GamesTabWrap = () => {
  const router = useRouter();
  const pathname = usePathname();
  const isMobileView = useIsMobileView();
  const isSearchPage = pathname.includes("search");
  const shouldShowTabMenu = !isSearchPage || (isSearchPage && !isMobileView);
  return (
    // `pt-4 laptop:pt-0` 는 종전에 스티키 래퍼가 갖고 있던 값이다 — 이 옛 배치의 여백이라
    // 여기로 옮겼다(#5568). 래퍼에 두면 `tabBar` 를 넘기는 리뉴얼 페이지에도 붙는다.
    <div className="mx-auto flex h-full w-full max-w-screen-max flex-col gap-3 pt-4 laptop:flex-row-reverse laptop:items-center laptop:justify-between laptop:gap-0 laptop:pt-0">
      <div
        className={cn(
          "mx-4 flex items-center gap-3 laptop:ml-0 max:mx-0",
          !shouldShowTabMenu && "pb-4",
        )}
      >
        {!shouldShowTabMenu && (
          <button onClick={() => router.back()}>
            <CarotLeftIcon />
          </button>
        )}

        <GamesSearchInput />
      </div>
      {shouldShowTabMenu && (
        <div className="flex flex-col gap-3 laptop:gap-0">
          <GamesTabMenu />
        </div>
      )}
    </div>
  );
};
